import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import * as moment from 'moment-timezone';
import { ToastrService } from 'ngx-toastr';
import { takeUntil } from 'rxjs/operators';
import { componentDestroyed } from '@w11k/ngx-componentdestroyed';
import { sweetAlert } from 'src/app/utils/swal';
import { StaffService } from 'src/app/component/staffs/staff-service/staff.service';

@Component({
  selector: 'app-staff-attendance',
  templateUrl: './staff-attendance.component.html',
  styleUrls: ['./staff-attendance.component.css'],
  animations: [
    trigger("expandActivity", [
      transition(":enter", [
        style({ opacity: 0, width: 0 }),
        animate("200ms ease-out", style({ opacity: 1, width: "*" }))
      ]),
      transition(":leave", [
        style({ opacity: 1, width: "*" }),
        animate("150ms ease-in", style({ opacity: 0, width: 0 }))
      ])
    ]),
    trigger("openCloseAttendance", [
      transition(":enter", [
        style({ opacity: 0, transform: "scale(0.95)" }),
        animate(
          "500ms ease-in-out",
          style({ opacity: 1, transform: "scale(1)" })
        )
      ]),
      transition(":leave", [
        style({ opacity: 1, }),
        animate(
          "300ms ease-in-out",
          style({ opacity: 0, transform: "scale(.95)" })
        )
      ])
    ])
  ]
})
export class StaffAttendanceComponent implements OnInit, OnChanges, OnDestroy {
  @Input() user: any;
  @Output() popoverOpened = new EventEmitter<void>();

  currentTime = moment().toISOString();
  intervalTimeID: any;
  todayShift: any = null;
  defaultShift: any = null;
  todayOverride: any = null;
  private overrideRequestId = 0;
  workedSeconds = 0;
  restOptions: Array<'10' | '20' | '30'> = ['10', '20', '30'];
  showActivity = false;
  activityCanScrollUp = false;
  activityCanScrollDown = false;
  private activityScrollEl: HTMLElement = null;

  @ViewChild('activityScroll', { static: false })
  set activityScrollRef(ref: ElementRef) {
    this.activityScrollEl = ref ? ref.nativeElement : null;
    if (this.activityScrollEl) {
      setTimeout(() => this.updateActivityScroll());
    }
  }
  showAttendancePopover: boolean = false;
  attendanceStatus: string = 'checked_out';
  checkInTime: string = null;
  checkOutTime: string = null;
  attendanceDurationDisplay: string = '00h 00m';
  activeBreakDurationDisplay: string = '0 mins';
  attendanceLoading: boolean = false;
  timeline: any = null;
  selectedBreakType: 'meal' | 'rest' = 'meal';
  selectedRestOption: '10' | '20' | '30' | 'custom' = '10';
  customRestEndTime: any = null;
  isOnBreak: boolean = false;
  activeBreak: any = null;
  private pendingRestBreakAutoEnd: boolean = false;
  private isAutoEndingRestBreak: boolean = false;
  private completedRestBreakId: string = null;

  get currentTimeZone(): string {
    return (this.user && this.user.timeZone) || (this.user && this.user.company && this.user.company.timeZone) || moment.tz.guess();
  }

  get isRestBreakActive(): boolean {
    if (!this.isOnBreak) {
      return false;
    }
    if (this.activeBreak) {
      const status = (this.activeBreak.status || '').toLowerCase();
      const label = (this.activeBreak.label || '').toLowerCase();
      if (status === 'rest' || label.indexOf('rest') !== -1 || !!this.activeBreak.paid) {
        return true;
      }
      if (status === 'open' || label.indexOf('meal') !== -1) {
        return false;
      }
    }
    return this.selectedBreakType === 'rest';
  }

  get activeBreakLabel(): string {
    if (this.activeBreak && this.activeBreak.label) {
      return this.activeBreak.label;
    }
    return this.selectedBreakType === 'meal' ? 'Meal Break' : 'Rest Break';
  }

  get activeBreakStartTime(): string {
    return (this.activeBreak && this.activeBreak.start) ? this.activeBreak.start : null;
  }

  get activeBreakStopTime(): string {
    return (this.activeBreak && this.activeBreak.stop) ? this.activeBreak.stop : null;
  }

  get startBreakButtonLabel(): string {
    return this.selectedBreakType === 'meal' ? 'Start Meal Break' : 'Start Rest Break';
  }

  get breakDurationLabel(): string {
    if (this.selectedBreakType !== 'rest') {
      return '';
    }
    if (this.selectedRestOption !== 'custom') {
      return this.selectedRestOption + ' min';
    }
    let hours = 0;
    let minutes = 0;
    if (moment.isMoment(this.customRestEndTime) || this.customRestEndTime instanceof Date) {
      const value = moment(this.customRestEndTime);
      hours = value.hour();
      minutes = value.minute();
    } else if (typeof this.customRestEndTime === 'string') {
      const parts = this.customRestEndTime.split(':');
      hours = parseInt(parts[0], 10) || 0;
      minutes = parseInt(parts[1], 10) || 0;
    }
    if (!hours && !minutes) {
      return '';
    }
    if (!hours) {
      return minutes + ' min';
    }
    return hours + 'h' + (minutes ? ' ' + minutes + 'm' : '');
  }

  get statusTone(): 'working' | 'break' | 'off' {
    if (this.attendanceStatus !== 'checked_in') {
      return 'off';
    }
    return this.isOnBreak ? 'break' : 'working';
  }

  get statusLabel(): string {
    if (this.statusTone === 'break') {
      return this.isRestBreakActive ? 'On Rest Break' : 'On Meal Break';
    }
    if (this.statusTone === 'working') {
      return 'Working';
    }
    return this.isNotCheckedIn ? 'Not Checked In' : 'Checked Out';
  }

  get isNotCheckedIn(): boolean {
    return this.statusTone === 'off' && !this.checkInTime && !this.checkOutTime;
  }

  get shiftStartMoment(): any {
    if (!this.todayShift || !this.todayShift.checkin) {
      return null;
    }
    const time = moment(this.todayShift.checkin, ['HH:mm', 'HHmm', 'hh:mm A', 'YYYY-MM-DDTHH:mm:ssZ']);
    if (!time.isValid()) {
      return null;
    }
    return moment(this.currentTime).tz(this.currentTimeZone).hour(time.hour()).minute(time.minute()).second(0);
  }

  get isShiftStarted(): boolean {
    const start = this.shiftStartMoment;
    return !!start && !moment(this.currentTime).isBefore(start);
  }

  get shiftStartsInLabel(): string {
    const start = this.shiftStartMoment;
    if (!start) {
      return '';
    }
    return this.formatShortDuration(start.diff(moment(this.currentTime), 'seconds'));
  }

  /** Staff can only check in when a shift is scheduled today and its start time has been reached. */
  get canCheckIn(): boolean {
    if (!this.todayShift) {
      return false;
    }
    return !this.shiftStartMoment || this.isShiftStarted;
  }

  get greeting(): string {
    const hour = moment(this.currentTime).tz(this.currentTimeZone).hour();
    const part = hour < 12 ? 'Good morning' : (hour < 17 ? 'Good afternoon' : 'Good evening');
    const name = this.user && this.user.displayName ? String(this.user.displayName).trim().split(/\s+/)[0] : '';
    return name ? part + ', ' + name : part;
  }

  get workedClock(): string {
    return this.formatClock(this.workedSeconds);
  }

  get breakElapsedSeconds(): number {
    if (!this.isOnBreak || !this.activeBreakStartTime) {
      return 0;
    }
    return Math.max(0, moment(this.currentTime).diff(moment(this.activeBreakStartTime), 'seconds'));
  }

  get breakElapsedClock(): string {
    return this.formatTimer(this.breakElapsedSeconds);
  }

  get restBreakRemainingSeconds(): number {
    if (!this.isRestBreakActive || !this.activeBreakStopTime) {
      return 0;
    }
    return Math.max(0, moment(this.activeBreakStopTime).diff(moment(this.currentTime), 'seconds'));
  }

  get restBreakRemainingClock(): string {
    return this.formatTimer(this.restBreakRemainingSeconds);
  }

  get restBreakPlannedLabel(): string {
    if (!this.activeBreakStartTime || !this.activeBreakStopTime) {
      return '';
    }
    const mins = Math.round(moment(this.activeBreakStopTime).diff(moment(this.activeBreakStartTime), 'seconds') / 60);
    if (mins <= 0) {
      return '';
    }
    return mins >= 60 ? Math.floor(mins / 60) + 'h ' + (mins % 60 ? (mins % 60) + 'm' : '') : mins + ' min';
  }

  get restBreakProgress(): number {
    if (!this.isRestBreakActive || !this.activeBreakStartTime || !this.activeBreakStopTime) {
      return 0;
    }
    const total = moment(this.activeBreakStopTime).diff(moment(this.activeBreakStartTime), 'seconds');
    return total > 0 ? Math.min(100, Math.round((this.breakElapsedSeconds / total) * 100)) : 100;
  }

  get sessionHistory(): any[] {
    if (!this.timeline && !this.checkInTime) {
      return [];
    }
    let sessions: any[] = (this.timeline && this.timeline.sessions && this.timeline.sessions.length)
      ? this.timeline.sessions.filter((s: any) => s && s.checkin)
      : [];
    if (!sessions.length && this.checkInTime) {
      sessions = [{ checkin: this.checkInTime, checkout: this.checkOutTime }];
    }
    const breaks: any[] = (this.timeline && this.timeline.breaks) ? this.timeline.breaks.filter((b: any) => b && b.start) : [];
    const now = moment(this.currentTime);

    const history = sessions.map((session: any, index: number) => {
      const start = moment(session.checkin);
      const end = session.checkout ? moment(session.checkout) : now;
      const sessionBreaks = breaks
        .filter((b: any) => {
          const bStart = moment(b.start);
          return bStart.isSameOrAfter(start) && bStart.isBefore(end);
        })
        .map((b: any) => {
          const isActive = b === this.activeBreak;
          // A break can't outlast its session (e.g. a rest break cut short by checking out).
          let bEnd = b.stop ? moment(b.stop) : now;
          if (bEnd.isAfter(end)) {
            bEnd = end;
          }
          const label = (b.label || '').toLowerCase();
          const isRest = b.status === 'rest' || label.indexOf('rest') !== -1;
          return {
            start: b.start,
            stop: isActive ? null : (b.stop ? bEnd.toISOString() : null),
            isActive: isActive,
            type: isRest ? 'rest' : 'meal',
            label: isRest ? 'Rest' : 'Meal',
            seconds: Math.max(0, bEnd.diff(moment(b.start), 'seconds'))
          };
        });
      const breakSeconds = sessionBreaks.reduce((sum: number, b: any) => sum + b.seconds, 0);
      return {
        number: index + 1,
        checkin: session.checkin,
        checkout: session.checkout || null,
        isActive: !session.checkout && this.attendanceStatus === 'checked_in',
        workedLabel: this.formatShortDuration(Math.max(0, end.diff(start, 'seconds') - breakSeconds)),
        breaks: sessionBreaks.map((b: any) => ({ ...b, durationLabel: this.formatShortDuration(b.seconds) }))
      };
    });
    // Newest session first so the current one is always at the top.
    return history.reverse();
  }

  toggleActivity(): void {
    this.showActivity = !this.showActivity;
  }

  updateActivityScroll(): void {
    const el = this.activityScrollEl;
    if (!el) {
      this.activityCanScrollUp = false;
      this.activityCanScrollDown = false;
      return;
    }
    this.activityCanScrollUp = el.scrollTop > 4;
    this.activityCanScrollDown = el.scrollTop + el.clientHeight < el.scrollHeight - 4;
  }

  scrollActivityDown(): void {
    if (this.activityScrollEl) {
      this.activityScrollEl.scrollBy({ top: this.activityScrollEl.clientHeight * 0.75, behavior: 'smooth' });
    }
  }

  trackByIndex(index: number): number {
    return index;
  }

  private formatShortDuration(totalSeconds: number): string {
    const mins = Math.floor(Math.max(0, totalSeconds) / 60);
    if (mins < 1) {
      return '<1m';
    }
    if (mins < 60) {
      return mins + 'm';
    }
    return mins % 60 ? Math.floor(mins / 60) + 'h ' + (mins % 60) + 'm' : Math.floor(mins / 60) + 'h';
  }

  get breaksTodayCount(): number {
    if (!this.timeline || !this.timeline.breaks) {
      return 0;
    }
    return this.timeline.breaks.filter((b: any) => b && b.start).length;
  }

  get breakTimeTodayDisplay(): string {
    if (!this.timeline || !this.timeline.breaks) {
      return '0m';
    }
    const now = moment(this.currentTime);
    let total = 0;
    this.timeline.breaks.forEach((b: any) => {
      if (!b || !b.start) {
        return;
      }
      let end = b.stop ? moment(b.stop) : (b === this.activeBreak ? now : null);
      if (!end) {
        return;
      }
      if (end.isAfter(now)) {
        end = now;
      }
      // Don't count past the check-out of the session the break belongs to.
      const bStart = moment(b.start);
      const session = (this.timeline.sessions || []).find((s: any) =>
        s && s.checkin && s.checkout && bStart.isSameOrAfter(moment(s.checkin)) && bStart.isBefore(moment(s.checkout)));
      if (session && end.isAfter(moment(session.checkout))) {
        end = moment(session.checkout);
      }
      total += Math.max(0, end.diff(bStart, 'seconds'));
    });
    return this.formatShortDuration(total);
  }

  private formatClock(totalSeconds: number): string {
    const sec = Math.max(0, Math.floor(totalSeconds));
    const pad = (n: number) => (n < 10 ? '0' + n : '' + n);
    return pad(Math.floor(sec / 3600)) + ':' + pad(Math.floor((sec % 3600) / 60)) + ':' + pad(sec % 60);
  }

  private formatTimer(totalSeconds: number): string {
    return totalSeconds >= 3600 ? this.formatClock(totalSeconds) : this.formatClock(totalSeconds).substring(3);
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    this.showAttendancePopover = false;
  }

  constructor(
    private toastr: ToastrService,
    private staffService: StaffService
  ) { }

  ngOnInit() {
    this.initCustomRestTimes();
    this.intervalTimeID = setInterval(() => {
      this.currentTime = moment().tz(this.currentTimeZone).toISOString();
      if (this.user && this.user.sRoles && this.attendanceStatus === 'checked_in') {
        this.updateAttendanceDuration();
      }
      if (this.showActivity) {
        this.updateActivityScroll();
      }
    }, 1000);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.user && this.user) {
      this.loadAttendanceState();
      this.loadTodayShift();
    }
  }

  ngOnDestroy(): void {
    if (this.intervalTimeID) {
      clearInterval(this.intervalTimeID);
    }
  }

  closePopover(): void {
    this.showAttendancePopover = false;
    this.showActivity = false;
  }

  toggleAttendancePopover(): void {
    this.showAttendancePopover = !this.showAttendancePopover;
    if (this.showAttendancePopover) {
      this.showActivity = false;
      this.popoverOpened.emit();
      if (this.selectedRestOption === 'custom') {
        this.refreshCustomRestTimeIfPast();
      }
      this.loadTodayShift();
    }
  }

  onAttendanceOutsideClick(event: MouseEvent): void {
    if (event && event.target) {
      const target = event.target as HTMLElement;
      if (target && typeof target.closest === 'function') {
        if (
          target.closest('.owl-dt-container') ||
          target.closest('.owl-dt-overlay') ||
          target.closest('.cdk-overlay-container') ||
          target.closest('.swal2-container')
        ) {
          return;
        }
      }
    }
    this.showAttendancePopover = false;
  }

  resetAttendanceState(): void {
    this.timeline = null;
    this.checkInTime = null;
    this.checkOutTime = null;
    this.attendanceStatus = 'checked_out';
    this.isOnBreak = false;
    this.activeBreak = null;
    this.pendingRestBreakAutoEnd = false;
    this.isAutoEndingRestBreak = false;
    this.setWorkedDuration(0);
    this.activeBreakDurationDisplay = '0 mins';
    this.completedRestBreakId = null;
  }

  loadAttendanceState(): void {
    if (!this.user || !this.user.sRoles) {
      return;
    }
    const today = moment().tz(this.currentTimeZone).toISOString();
    this.staffService
      .getStaffTimeline('?startDate=' + today)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (res: any) => {
          let data = res && res.data ? res.data : res;
          if (data && data.length) {
            data = data[data.length - 1];
          }
          if (data && (data.checkin || data._id || (data.sessions && data.sessions.length))) {
            this.updateAttendanceState(data);
          } else {
            this.resetAttendanceState();
          }
        },
        () => {
          this.resetAttendanceState();
        }
      );
  }

  loadTodayShift(): void {
    if (this.user && this.user.shifts && this.user.shifts.length) {
      const currentDay = moment().tz(this.currentTimeZone).day();
      this.defaultShift = this.user.shifts.find((s: any) => Boolean(s.isEnabled) && Number(s.day) === currentDay) || null;
    } else {
      this.defaultShift = null;
    }
    // Keep showing the last known override while today's is re-fetched, so the shift doesn't flicker.
    this.applyTodayShift();
    this.loadTodayShiftOverride();
  }

  /** An approved, active override for today takes the place of the default weekly shift. */
  loadTodayShiftOverride(): void {
    const staffId = this.user ? (this.user.sId || this.user._id) : null;
    if (!staffId) {
      return;
    }
    const tz = this.currentTimeZone;
    const day = moment().tz(tz).format('YYYY-MM-DD');
    const requestId = ++this.overrideRequestId;
    const payload = {
      startDate: moment().tz(tz).startOf('day').toISOString(),
      endDate: moment().tz(tz).endOf('day').toISOString(),
      staffIds: [staffId],
      status: 'active'
    };
    this.staffService
      .getShiftOverridesByDate(payload)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (res: any) => {
          // A newer request (or a new day) has superseded this response.
          if (requestId !== this.overrideRequestId || day !== moment().tz(this.currentTimeZone).format('YYYY-MM-DD')) {
            return;
          }
          const list: any[] = Array.isArray(res) ? res : (res && Array.isArray(res.data) ? res.data : []);
          const overrides = list
            .filter((o: any) => o && o.shift && o.status === 'active' && o.approvalStatus === 'approved')
            .sort((a: any, b: any) => moment(b.updatedAt || b.createdAt).valueOf() - moment(a.updatedAt || a.createdAt).valueOf());
          this.todayOverride = overrides.length ? overrides[0] : null;
          this.applyTodayShift();
        },
        () => {
          // Fall back to the default shift if overrides can't be loaded.
          if (requestId === this.overrideRequestId) {
            this.todayOverride = null;
            this.applyTodayShift();
          }
        }
      );
  }

  private applyTodayShift(): void {
    const override = this.todayOverride;
    if (!override) {
      this.todayShift = this.defaultShift;
      return;
    }
    // isEnabled on an override means the staff is marked "Not Available" for the day.
    if (override.isEnabled) {
      this.todayShift = null;
      return;
    }
    this.todayShift = {
      checkin: override.shift.checkin,
      checkout: override.shift.checkout,
      isOverride: true
    };
  }

  get isUnavailableToday(): boolean {
    return !!(this.todayOverride && this.todayOverride.isEnabled);
  }

  formatShiftTime(timeStr: string): string {
    if (!timeStr) {
      return '';
    }
    const format = (this.user && this.user.timeFormat) ? this.user.timeFormat : 'hh:mm A';
    const parsed = moment(timeStr, ['HH:mm', 'HHmm', 'hh:mm A', 'YYYY-MM-DDTHH:mm:ssZ']);
    if (parsed.isValid()) {
      return parsed.format(format);
    }
    return timeStr;
  }

  selectBreakType(type: 'meal' | 'rest'): void {
    this.selectedBreakType = type;
    if (type === 'rest' && this.selectedRestOption === 'custom') {
      this.refreshCustomRestTimeIfPast();
    }
  }

  selectRestOption(option: '10' | '20' | '30' | 'custom'): void {
    this.selectedRestOption = option;
    if (option === 'custom' && !this.customRestEndTime) {
      this.initCustomRestTimes();
    }
  }

  refreshCustomRestTimeIfPast(): void {
    if (!this.customRestEndTime) {
      this.initCustomRestTimes();
    }
  }

  initCustomRestTimes(): void {
    const tz = this.currentTimeZone;
    this.customRestEndTime = moment().tz(tz).startOf('day');
  }

  autoEndRestBreak(breakItem?: any): void {
    this.isAutoEndingRestBreak = false;
    this.isOnBreak = false;
    this.activeBreak = null;
    this.activeBreakDurationDisplay = '0 mins';
    this.pendingRestBreakAutoEnd = false;
    this.selectedBreakType = 'meal';

    const tz = this.currentTimeZone;
    const stopTime = (breakItem && breakItem.stop) ? breakItem.stop : moment().tz(tz).toISOString();

    if (this.timeline && this.timeline.breaks && this.timeline.breaks.length && breakItem) {
      const endingStart = breakItem.start;
      const endingId = breakItem._id;
      for (let i = 0; i < this.timeline.breaks.length; i++) {
        const b = this.timeline.breaks[i];
        if (b && ((endingId && b._id === endingId) || (endingStart && b.start === endingStart))) {
          b.status = 'rest';
          if (!b.stop) {
            b.stop = stopTime;
          }
        }
      }
    }

    if (this.timeline && (this.timeline.status === 'open' || this.timeline.status === 'rest')) {
      this.timeline.status = 'approved';
    }

    this.toastr.info('Rest break ended. Shift resumed.', 'Attendance');
  }

  /**
   * The API records each break as several entries: an "open" one when it starts and a
   * closed one (with stop) when it ends. They share the same start but not always the
   * same _id. Collapse them to one entry per break, keeping the most complete record.
   */
  consolidateBreaks(breaks: any[]): any[] {
    if (!breaks || !breaks.length) {
      return [];
    }
    const result: any[] = [];
    breaks.forEach((b: any) => {
      if (!b || !b.start) {
        return;
      }
      const index = result.findIndex((existing: any) => this.isSameBreak(existing, b));
      if (index === -1) {
        result.push(b);
      } else if (this.isMoreCompleteBreak(b, result[index])) {
        result[index] = b;
      }
    });
    return result.sort((x: any, y: any) => moment(x.start).valueOf() - moment(y.start).valueOf());
  }

  private isSameBreak(a: any, b: any): boolean {
    if (a._id && b._id && a._id === b._id) {
      return true;
    }
    const aStart = moment(a.start);
    const bStart = moment(b.start);
    if (Math.abs(aStart.diff(bStart, 'seconds')) > 30) {
      return false;
    }
    // Starts are close, but if one break ended before the other began they're distinct breaks.
    if (a.stop && bStart.isAfter(moment(a.stop))) {
      return false;
    }
    if (b.stop && aStart.isAfter(moment(b.stop))) {
      return false;
    }
    return true;
  }

  private isMoreCompleteBreak(candidate: any, current: any): boolean {
    const score = (x: any) => (x.stop ? 2 : 0) + (x.status === 'approved' || x.status === 'completed' ? 1 : 0);
    const diff = score(candidate) - score(current);
    if (diff !== 0) {
      return diff > 0;
    }
    // Same completeness: the most recently updated entry wins.
    const cTime = candidate.updatedAt ? moment(candidate.updatedAt).valueOf() : 0;
    const eTime = current.updatedAt ? moment(current.updatedAt).valueOf() : 0;
    return cTime >= eTime;
  }

  checkActiveBreak(): void {
    this.activeBreak = null;
    this.isOnBreak = false;
    if (!this.timeline || !this.timeline.breaks || !this.timeline.breaks.length || this.attendanceStatus !== 'checked_in') {
      return;
    }

    this.timeline.breaks = this.consolidateBreaks(this.timeline.breaks);

    if (this.timeline.status === 'completed' || this.timeline.checkout) {
      return;
    }

    let activeSessionStart: any = null;
    if (this.timeline.sessions && this.timeline.sessions.length) {
      const lastSession = this.timeline.sessions[this.timeline.sessions.length - 1];
      if (lastSession && lastSession.checkin && !lastSession.checkout) {
        activeSessionStart = moment(lastSession.checkin);
      }
    }
    if (!activeSessionStart && this.checkInTime) {
      activeSessionStart = moment(this.checkInTime);
    }

    const now = moment().tz(this.currentTimeZone);
    for (let i = this.timeline.breaks.length - 1; i >= 0; i--) {
      const b = this.timeline.breaks[i];
      if (!b) {
        continue;
      }
      if (b.status === 'approved' || b.status === 'completed') {
        continue;
      }
      if (activeSessionStart && b.start && moment(b.start).isBefore(activeSessionStart)) {
        continue;
      }

      if (b.stop) {
        const stopMoment = moment(b.stop).tz(this.currentTimeZone);
        if (now.isBefore(stopMoment) && (b.status === 'rest' || b.label === 'Rest break' || this.timeline.status === 'rest')) {
          this.activeBreak = b;
          this.isOnBreak = true;
          this.pendingRestBreakAutoEnd = true;
          return;
        }
        continue;
      }

      if (b.status === 'open' || b.status === 'rest' || this.timeline.status === 'open' || this.timeline.status === 'rest') {
        this.activeBreak = b;
        this.isOnBreak = true;
        return;
      }
    }

    this.isOnBreak = false;
    this.activeBreak = null;
    this.pendingRestBreakAutoEnd = false;

    if (this.timeline.status === 'open' || this.timeline.status === 'rest') {
      this.timeline.status = 'approved';
    }
  }

  updateAttendanceState(res: any): void {
    let data = res && res.data ? res.data : res;
    if (data && data.length) {
      data = data[data.length - 1];
    }
    if (!data) {
      return;
    }
    this.timeline = data;

    if (this.timeline.breaks && this.timeline.breaks.length) {
      this.timeline.breaks = this.consolidateBreaks(this.timeline.breaks);
    }

    let latestSession: any = null;
    if (data.sessions && data.sessions.length) {
      latestSession = data.sessions[data.sessions.length - 1];
    }

    if (data.status === 'completed' || data.checkout) {
      this.attendanceStatus = 'checked_out';
      this.checkOutTime = data.checkout || (latestSession ? latestSession.checkout : null);
      this.checkInTime = (latestSession && latestSession.checkin) ? latestSession.checkin : (data.checkin || null);
    } else if (latestSession && latestSession.checkin && !latestSession.checkout) {
      this.checkInTime = latestSession.checkin;
      this.checkOutTime = null;
      this.attendanceStatus = 'checked_in';
    } else if (latestSession && latestSession.checkout) {
      this.checkInTime = latestSession.checkin || data.checkin || null;
      this.checkOutTime = latestSession.checkout;
      this.attendanceStatus = 'checked_out';
    } else {
      this.checkInTime = data.checkin || null;
      this.checkOutTime = data.checkout || null;
      this.attendanceStatus = (this.checkInTime && !this.checkOutTime) ? 'checked_in' : 'checked_out';
    }

    this.checkActiveBreak();
    this.updateAttendanceDuration();
  }

  updateAttendanceDuration(): void {
    if (this.attendanceStatus === 'checked_in') {
      const wasRestBreakActive = this.isOnBreak && this.isRestBreakActive && !!this.activeBreak;
      const breakToEnd = this.activeBreak;
      this.checkActiveBreak();

      if (wasRestBreakActive && !this.isOnBreak && breakToEnd) {
        const breakKey = breakToEnd._id ? breakToEnd._id : breakToEnd.start;
        if (breakKey && this.completedRestBreakId !== breakKey) {
          this.completedRestBreakId = breakKey;
          this.autoEndRestBreak(breakToEnd);
        }
      }

      if (this.isOnBreak && this.activeBreak && this.activeBreak.start) {
        const breakStart = moment(this.activeBreak.start);
        const now = moment().tz(this.currentTimeZone);
        const breakSec = Math.max(0, now.diff(breakStart, 'seconds'));
        const breakHours = Math.floor(breakSec / 3600);
        const breakMinutes = Math.floor((breakSec % 3600) / 60);
        if (breakHours > 0) {
          const breakMStr = breakMinutes < 10 ? '0' + breakMinutes : '' + breakMinutes;
          this.activeBreakDurationDisplay = breakHours + 'h ' + breakMStr + 'm';
        } else if (breakMinutes === 1) {
          this.activeBreakDurationDisplay = '1 min';
        } else {
          this.activeBreakDurationDisplay = breakMinutes + ' mins';
        }
      } else {
        this.activeBreakDurationDisplay = '0 mins';
      }

      if (this.isOnBreak && this.activeBreak && this.isRestBreakActive && this.activeBreak.stop) {
        const stopMoment = moment(this.activeBreak.stop).tz(this.currentTimeZone);
        const now = moment().tz(this.currentTimeZone);
        if (now.isSameOrAfter(stopMoment)) {
          const breakKey = this.activeBreak._id ? this.activeBreak._id : this.activeBreak.start;
          if (breakKey && this.completedRestBreakId !== breakKey) {
            this.completedRestBreakId = breakKey;
            const currentBreak = this.activeBreak;
            this.autoEndRestBreak(currentBreak);
          }
        }
      }

      let totalSeconds = 0;
      let activeSessionStart: any = null;

      if (this.timeline && this.timeline.sessions && this.timeline.sessions.length) {
        for (let i = 0; i < this.timeline.sessions.length; i++) {
          const s = this.timeline.sessions[i];
          if (!s || !s.checkin) {
            continue;
          }
          if (s.checkout) {
            const sStart = moment(s.checkin);
            const sEnd = moment(s.checkout);
            let sBreakSec = 0;
            const sProcessedStarts: any = {};
            if (this.timeline.breaks && this.timeline.breaks.length) {
              for (let j = 0; j < this.timeline.breaks.length; j++) {
                const b = this.timeline.breaks[j];
                if (b && b.start && b.stop) {
                  const bKey = (b._id ? b._id + '_' : '') + b.start;
                  if (sProcessedStarts[bKey]) {
                    continue;
                  }
                  sProcessedStarts[bKey] = true;
                  const bStart = moment(b.start);
                  const bStop = moment(b.stop);
                  if (bStart.isSameOrAfter(sStart) && bStop.isSameOrBefore(sEnd)) {
                    sBreakSec += Math.max(0, bStop.diff(bStart, 'seconds'));
                  }
                }
              }
            }
            totalSeconds += Math.max(0, sEnd.diff(sStart, 'seconds') - sBreakSec);
          } else {
            activeSessionStart = moment(s.checkin);
          }
        }
      }

      if (!activeSessionStart && this.checkInTime) {
        activeSessionStart = moment(this.checkInTime);
      }

      if (activeSessionStart) {
        let endMoment: any;
        if (this.isOnBreak && this.activeBreak && this.activeBreak.start) {
          endMoment = moment(this.activeBreak.start);
        } else {
          endMoment = moment().tz(this.currentTimeZone);
        }

        let activeSessionBreakSec = 0;
        const activeProcessedStarts: any = {};
        if (this.timeline && this.timeline.breaks && this.timeline.breaks.length) {
          for (let i = 0; i < this.timeline.breaks.length; i++) {
            const b = this.timeline.breaks[i];
            if (b && b !== this.activeBreak && b.start && b.stop) {
              const bKey = (b._id ? b._id + '_' : '') + b.start;
              if (activeProcessedStarts[bKey]) {
                continue;
              }
              activeProcessedStarts[bKey] = true;
              const bStart = moment(b.start);
              const bStop = moment(b.stop);
              if (bStart.isSameOrAfter(activeSessionStart)) {
                const effectiveStop = bStop.isBefore(endMoment) ? bStop : endMoment;
                if (effectiveStop.isAfter(bStart)) {
                  activeSessionBreakSec += Math.max(0, effectiveStop.diff(bStart, 'seconds'));
                }
              }
            }
          }
        }

        const currentSessionWorkingSec = Math.max(0, endMoment.diff(activeSessionStart, 'seconds') - activeSessionBreakSec);
        totalSeconds += currentSessionWorkingSec;
      }

      this.setWorkedDuration(totalSeconds);
    } else if (this.timeline && this.timeline.sessions && this.timeline.sessions.length) {
      let totalSeconds = 0;
      for (let i = 0; i < this.timeline.sessions.length; i++) {
        const s = this.timeline.sessions[i];
        if (s && s.checkin && s.checkout) {
          const sStart = moment(s.checkin);
          const sEnd = moment(s.checkout);
          let sBreakSec = 0;
          const sProcessedStarts: any = {};
          if (this.timeline.breaks && this.timeline.breaks.length) {
            for (let j = 0; j < this.timeline.breaks.length; j++) {
              const b = this.timeline.breaks[j];
              if (b && b.start && b.stop) {
                const bKey = (b._id ? b._id + '_' : '') + b.start;
                if (sProcessedStarts[bKey]) {
                  continue;
                }
                sProcessedStarts[bKey] = true;
                const bStart = moment(b.start);
                const bStop = moment(b.stop);
                if (bStart.isSameOrAfter(sStart) && bStop.isSameOrBefore(sEnd)) {
                  sBreakSec += Math.max(0, bStop.diff(bStart, 'seconds'));
                }
              }
            }
          }
          totalSeconds += Math.max(0, sEnd.diff(sStart, 'seconds') - sBreakSec);
        }
      }
      this.setWorkedDuration(totalSeconds);
    } else if (this.timeline && this.timeline.workTimeInMillis) {
      this.setWorkedDuration(Math.floor(this.timeline.workTimeInMillis / 1000));
    } else if (this.checkInTime && this.checkOutTime) {
      const start = moment(this.checkInTime);
      const end = moment(this.checkOutTime);
      const totalSeconds = Math.max(0, end.diff(start, 'seconds'));
      this.setWorkedDuration(totalSeconds);
    } else {
      this.setWorkedDuration(0);
      this.activeBreakDurationDisplay = '0 mins';
    }
  }

  private setWorkedDuration(totalSeconds: number): void {
    this.workedSeconds = Math.max(0, totalSeconds);
    const hours = Math.floor(this.workedSeconds / 3600);
    const minutes = Math.floor((this.workedSeconds % 3600) / 60);
    const hStr = hours < 10 ? '0' + hours : '' + hours;
    const mStr = minutes < 10 ? '0' + minutes : '' + minutes;
    this.attendanceDurationDisplay = hStr + 'h ' + mStr + 'm';
  }

  onStartBreak(): void {
    if (this.attendanceLoading || this.isOnBreak) {
      return;
    }
    this.completedRestBreakId = null;
    this.attendanceLoading = true;
    const tz = this.currentTimeZone;
    const now = moment().tz(tz);
    let payload: any;

    if (this.selectedBreakType === 'meal') {
      payload = {
        status: 'open',
        break: {
          label: 'Meal break',
          status: 'open',
          start: now.toISOString()
        }
      };
    } else {
      let startIso: string;
      let stopIso: string;

      if (this.selectedRestOption === 'custom') {
        if (!this.customRestEndTime) {
          this.attendanceLoading = false;
          this.toastr.warning('Please select a break duration', 'Rest Break');
          return;
        }

        let durationHours = 0;
        let durationMinutes = 0;

        if (moment.isMoment(this.customRestEndTime) || this.customRestEndTime instanceof Date) {
          const endM = moment(this.customRestEndTime);
          durationHours = endM.hour();
          durationMinutes = endM.minute();
        } else if (typeof this.customRestEndTime === 'string') {
          const endParts = this.customRestEndTime.split(':');
          if (endParts.length >= 2) {
            durationHours = parseInt(endParts[0], 10) || 0;
            durationMinutes = parseInt(endParts[1], 10) || 0;
          }
        }

        const totalMinutes = (durationHours * 60) + durationMinutes;
        if (totalMinutes <= 0) {
          this.attendanceLoading = false;
          this.toastr.warning('Please enter a duration greater than 0', 'Rest Break');
          return;
        }

        startIso = now.toISOString();
        stopIso = now.clone().add(durationHours, 'hours').add(durationMinutes, 'minutes').toISOString();
      } else {
        const mins = parseInt(this.selectedRestOption, 10) || 10;
        startIso = now.toISOString();
        stopIso = now.clone().add(mins, 'minutes').toISOString();
      }

      payload = {
        status: 'rest',
        break: {
          label: 'Rest break',
          status: 'rest',
          start: startIso,
          stop: stopIso,
          // Planned length of the rest break, in ms.
          duration: Math.max(0, moment(stopIso).diff(moment(startIso)))
        }
      };
    }

    this.staffService
      .saveStaffBreakTimeline(payload)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (res: any) => {
          this.attendanceLoading = false;
          if (this.selectedBreakType === 'rest') {
            this.pendingRestBreakAutoEnd = true;
          }
          let data = res && res.data ? res.data : res;
          if (data && data.length) {
            data = data[data.length - 1];
          }
          if (data && (data.checkin || data._id || (data.sessions && data.sessions.length))) {
            this.updateAttendanceState(data);
          } else {
            this.loadAttendanceState();
          }
          const breakName = this.selectedBreakType === 'meal' ? 'Meal break' : 'Rest break';
          this.toastr.success(breakName + ' started', 'Attendance');
          this.initCustomRestTimes();
        },
        (err: any) => {
          this.attendanceLoading = false;
          this.toastr.error(err && err.message ? err.message : 'Failed to start break', 'Error');
        }
      );
  }

  onEndBreak(): void {
    if (this.attendanceLoading || !this.isOnBreak) {
      return;
    }
    const message = this.isRestBreakActive
      ? 'End your rest break early? You have ' + this.restBreakRemainingClock + ' left.'
      : 'End your meal break and resume work?';
    sweetAlert(
      'Confirmation',
      message,
      'warning',
      'Yes',
      'No'
    ).then((result: any) => {
      if (result && result.value) {
        this.submitEndBreak();
      }
    });
  }

  private submitEndBreak(): void {
    if (this.attendanceLoading || !this.isOnBreak) {
      return;
    }
    this.completedRestBreakId = null;
    this.attendanceLoading = true;
    const tz = this.currentTimeZone;
    const stopTime = moment().tz(tz).toISOString();
    const breakEnding = this.activeBreak;
    const isMeal = !this.isRestBreakActive && (!breakEnding || breakEnding.label === 'Meal break' || breakEnding.status === 'open');
    const breakStart = (breakEnding && breakEnding.start) ? breakEnding.start : stopTime;
    // Actual time spent on the break, in ms.
    const duration = Math.max(0, moment(stopTime).diff(moment(breakStart)));

    let payload: any;

    if (!isMeal && breakEnding && breakEnding._id) {
      // Rest breaks are ended by id; the backend closes the break and stamps the stop time.
      payload = {
        endBreak: { _id: breakEnding._id, duration: duration }
      };
    } else {
      payload = {
        status: 'approved',
        break: {
          label: (breakEnding && breakEnding.label) ? breakEnding.label : (isMeal ? 'Meal break' : 'Rest break'),
          status: isMeal ? 'approved' : 'rest',
          start: breakStart,
          stop: stopTime,
          duration: duration
        }
      };
    }

    this.staffService
      .saveStaffBreakTimeline(payload)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (res: any) => {
          this.attendanceLoading = false;
          this.isOnBreak = false;
          this.activeBreak = null;
          this.activeBreakDurationDisplay = '0 mins';
          this.pendingRestBreakAutoEnd = false;
          this.selectedBreakType = 'meal';

          let data = res && res.data ? res.data : res;
          if (data && data.length) {
            data = data[data.length - 1];
          }

          if (data && data.breaks && data.breaks.length && breakEnding) {
            const endingStart = breakEnding.start;
            const endingId = breakEnding._id;
            for (let i = 0; i < data.breaks.length; i++) {
              const b = data.breaks[i];
              if (b && ((endingId && b._id === endingId) || (endingStart && b.start === endingStart))) {
                b.status = isMeal ? 'approved' : 'rest';
                // A rest break ended early may still carry its scheduled stop; cap it so it isn't seen as active.
                if (!b.stop || moment(b.stop).isAfter(moment(stopTime))) {
                  b.stop = stopTime;
                }
              }
            }
          }

          if (data && (data.checkin || data._id || (data.sessions && data.sessions.length))) {
            this.updateAttendanceState(data);
          } else {
            this.loadAttendanceState();
          }
          this.toastr.success('Break ended successfully', 'Attendance');
        },
        (err: any) => {
          this.attendanceLoading = false;
          this.toastr.error(err && err.message ? err.message : 'Failed to end break', 'Error');
        }
      );
  }

  onStaffCheckIn(): void {
    if (this.attendanceLoading) {
      return;
    }
    if (!this.todayShift) {
      const message = this.isUnavailableToday
        ? 'You are marked as not available today. Please contact your administrator.'
        : 'No shift is scheduled for you today. Please contact your administrator.';
      this.toastr.warning(message, 'Attendance');
      return;
    }
    if (!this.canCheckIn) {
      this.toastr.warning('You can check in once your shift starts at ' + this.formatShiftTime(this.todayShift.checkin) + '.', 'Attendance');
      return;
    }
    this.completedRestBreakId = null;
    this.attendanceLoading = true;
    const tz = this.currentTimeZone;
    const payload = {
      checkin: moment().tz(tz).toISOString()
    };
    this.staffService
      .saveStaffTimeline(payload)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (res: any) => {
          this.attendanceLoading = false;
          let data = res && res.data ? res.data : res;
          if (data && data.length) {
            data = data[data.length - 1];
          }
          if (data && (data.checkin || data._id || (data.sessions && data.sessions.length))) {
            this.updateAttendanceState(data);
          } else {
            this.loadAttendanceState();
          }
          this.toastr.success('Checked in successfully', 'Attendance');
        },
        (err: any) => {
          this.attendanceLoading = false;
          this.toastr.error(err && err.message ? err.message : 'Failed to check in', 'Error');
        }
      );
  }

  onStaffCheckOut(): void {
    if (this.isOnBreak) {
      this.toastr.warning('Please end your break before checking out', 'Attendance');
      return;
    }
    sweetAlert(
      'Confirmation',
      'Are you sure you want to end your shift?',
      'warning',
      'Yes',
      'No'
    ).then((result: any) => {
      if (result && result.value) {
        this.completedRestBreakId = null;
        this.attendanceLoading = true;
        const tz = this.currentTimeZone;
        const payload = {
          checkout: moment().tz(tz).toISOString()
        };
        this.staffService
          .saveStaffTimeline(payload)
          .pipe(takeUntil(componentDestroyed(this)))
          .subscribe(
            (res: any) => {
              this.attendanceLoading = false;
              this.showAttendancePopover = false;
              let data = res && res.data ? res.data : res;
              if (data && data.length) {
                data = data[data.length - 1];
              }
              if (data && (data.checkin || data._id || (data.sessions && data.sessions.length))) {
                this.updateAttendanceState(data);
              } else {
                this.loadAttendanceState();
              }
              this.toastr.success('Checked out successfully', 'Attendance');
            },
            (err: any) => {
              this.attendanceLoading = false;
              this.toastr.error(err && err.message ? err.message : 'Failed to check out', 'Error');
            }
          );
      }
    });
  }
}
